import Button from '@components/button/Button';
import Icon from '@components/icon/Icon';
import { ButtonSize, ButtonVariant } from '@types';

import Modal from './Modal';

interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  message?: string;
  iconName?: string;
  ModalIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  cancelButtonLabel?: string;
  confirmButtonLabel?: string;
  cancelButtonStyle?: string;
  confirmButtonStyle?: string;
  handleConfirm: () => void;
  handleCancel: () => void;
  className?: string;
  isConfirmButtonLoading?: boolean;
  children?: any;
  wrapperClass?: string;
  isConfirmButtonDisabled?: boolean;
  buttonIds?: { confirmButton?: string; cancelButton?: string };
  confirmButtonLabelStyle?: string;
  primaryButtonVariant?: ButtonVariant;
  primaryButtonSize?: ButtonSize;
  secondaryButtonVariant?: ButtonVariant;
  secondaryButtonSize?: ButtonSize;
  messageStyle?: string;
  isClickOutsideRequired?: boolean;
  customModalIconStyle?: string;
  isCancelButtonDisabled?: boolean;
  titleStyle?: string;
  cancelButtonLabelStyle?: string;
}

const ConfirmationModal = ({
  isOpen,
  title,
  message,
  iconName,
  ModalIcon,
  cancelButtonLabel = 'Cancel',
  confirmButtonLabel = 'Confirm',
  cancelButtonStyle = '',
  confirmButtonStyle = 'text-primary',
  handleConfirm,
  handleCancel,
  className,
  isConfirmButtonLoading,
  children,
  wrapperClass,
  isConfirmButtonDisabled,
  isCancelButtonDisabled,
  buttonIds,
  confirmButtonLabelStyle,
  primaryButtonVariant = 'transparent',
  primaryButtonSize = 'none',
  secondaryButtonVariant = 'transparent',
  secondaryButtonSize = 'none',
  messageStyle = '',
  isClickOutsideRequired = true,
  customModalIconStyle = 'size-8',
  titleStyle = '',
  cancelButtonLabelStyle = ''
}: ConfirmationModalProps) => (
  <Modal
    isOpen={isOpen}
    onCancel={handleCancel}
    className={`!rounded-xl ${wrapperClass}`}
    isClickOutsideRequired={isClickOutsideRequired}
  >
    <div className={`p-6 pb-0 ${className && className}`}>
      {iconName && (
        <div className="mb-4 flex items-center justify-center">
          <Icon name={iconName} size="extralarge" />
        </div>
      )}
      {ModalIcon && (
        <div className="mb-4 flex items-center justify-center">
          <ModalIcon className={customModalIconStyle} />
        </div>
      )}
      <div
        className={`mb-3 flex justify-center whitespace-pre-wrap break-normal text-center text-lg font-medium text-content ${titleStyle}`}
      >
        {title}
      </div>
      {message && (
        <div
          className={`flex justify-center whitespace-pre-wrap break-words text-center text-base font-normal text-grey ${
            messageStyle || ''
          }`}
        >
          {message}
        </div>
      )}
      {children && children}
      <div className="mt-3 flex gap-3">
        <Button
          data-testid={buttonIds?.cancelButton}
          handleClick={handleCancel}
          variant={secondaryButtonVariant || 'transparent'}
          size={secondaryButtonSize || 'none'}
          className={`${
            secondaryButtonVariant === 'transparent'
              ? 'w-1/2 py-6 pt-3 text-base font-medium'
              : 'w-1/2'
          } ${cancelButtonStyle || ''}`}
          label={cancelButtonLabel}
          disabled={isCancelButtonDisabled}
          labelStyle={cancelButtonLabelStyle}
        />
        <Button
          data-testid={buttonIds?.confirmButton}
          handleClick={handleConfirm}
          variant={primaryButtonVariant || 'transparent'}
          size={primaryButtonSize || 'none'}
          className={`${
            primaryButtonVariant === 'transparent'
              ? 'w-1/2 py-6 pt-3 text-base font-medium'
              : 'w-1/2'
          } ${confirmButtonStyle || ''}`}
          loading={isConfirmButtonLoading || false}
          label={confirmButtonLabel}
          labelStyle={confirmButtonLabelStyle}
          disabled={isConfirmButtonDisabled}
        />
      </div>
    </div>
  </Modal>
);

export default ConfirmationModal;
